import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  QUOTE_STATES,
  REPORT_PRESETS,
  REVENUE_BASES,
  serviceTypeFilterValue,
} from '@servwell/shared';
import { workspaceTableSearch } from './useWorkspaceTable';

/**
 * Tab state in the URL (105) — the app-wide gap 004/007 named, closed for the three
 * pages A-2 tabbed. One definition feeds both sides of the contract: the route's
 * `validateSearch` (what a deep link may say) and the page's reader/writer hook (what
 * a click writes back). A page inventing its own list would drift from its route's.
 *
 * The DEFAULT tab never appears in a URL this hook WRITES, and `tabSearch` reads the
 * default (and junk) as `{}` in state — so links the app produces have one canonical
 * address. A hand-typed `?tab=bogus` behaves correctly but sits in the address bar
 * until the next tab click rewrites it; the router does not rewrite URLs on load.
 */
export const MONEY_TABS = ['invoices', 'payments', 'books'] as const;
export const JOB_TABS = ['overview', 'work', 'money', 'notes'] as const;
export const COMMERCIAL_JOB_SECTIONS = ['activity', 'details', 'related'] as const;
export const commercialJobSearch = (search: Record<string, unknown>) => ({
  ...tabSearch(JOB_TABS)(search),
  ...tabSearch(COMMERCIAL_JOB_SECTIONS, 'section')(search),
});
export const JOB_WORKSPACE_VIEWS = ['list', 'pipeline', 'repeating'] as const;
export const CUSTOMER_TABS = ['overview', 'equipment', 'notes'] as const;
/**
 * The register, and the two things it owes (107). Deliberately one page with three tabs
 * rather than three routes: the two due tables are views OF the register, and 112's
 * fleet-overview cards need a "View all" address to send somebody to — `?tab=maintenance`
 * is that address.
 */
export const ASSET_TABS = ['assets', 'maintenance', 'reminders'] as const;
/**
 * The dispatch page's two boards (121). `day` is first and is therefore the DEFAULT, so
 * `/schedule` and `/schedule?tab=day` are one address rather than two.
 *
 * ⚠️ Two BOARDS, not two views of one: the day board is rig lanes × hours, the week board is
 * rig lanes × days. 120 established they answer different questions ("who has room Thursday"
 * versus "what does 10am look like"), which is why the week grid is its own component rather
 * than the day board given a wider range.
 *
 * `map` (ops insights Phase 4, G-M3): the crew map, a third tab rather than a page. It is
 * always the shop's TODAY [Q4], whatever day the Day board is on, so it has no day of its own
 * to put in the address.
 */
export const SCHEDULE_TABS = ['day', 'week', 'map'] as const;
/**
 * The fleet page itself (112, approved prototype D1): the shop's vans as a dashboard, and
 * the same vans as the day they are about to drive. `overview` is first and is therefore
 * the default — it never appears in a URL the app writes, so `/fleet` and
 * `/fleet?tab=overview` are one address rather than two.
 *
 * ⚠️ Two tabs, not three: the REGISTER stays at `/fleet/assets` with its own three
 * (`ASSET_TABS` above). Folding it in as a fourth tab here would have put a paged server
 * table inside a panel that must stay mounted, and it already has an address the whole app
 * links to — including this page's own "View all".
 */
export const FLEET_TABS = ['overview', 'routes'] as const;
/**
 * The office landing (117, approved prototype A3/A3b). `overview` is the default and never
 * appears in a URL the app writes, so `/today` and `/today?tab=overview` are one address.
 *
 * ⚠️ `attention` exists as an ADDRESS, which is the point of it being a tab rather than a
 * "show more" toggle: a notification, an email or a colleague's link can land somebody on
 * the queue itself. The Overview card's "View all" is that link.
 */
export const TODAY_TABS = ['overview', 'attention'] as const;

/**
 * The Revenue page's period (`/reports/revenue?period=ytd`, ops insights Phase 2), on the same
 * contract under its own parameter name: the presets every report offers, This month first and
 * so the default, never written into a link. A link somebody sends opens the same view.
 */
export const REPORT_PERIODS = REPORT_PRESETS.map((p) => p.key);

const has = (valid: readonly string[], t: unknown): t is string =>
  typeof t === 'string' && valid.some((v) => v === t);

/**
 * A `validateSearch` for one route's `?tab=` (or another choice under its own `param`): junk,
 * absence and the default all → `{}`.
 */
export function tabSearch<T extends readonly string[], P extends string = 'tab'>(
  valid: T,
  // The search parameter it validates: `tab` on a tabbed page, `period` on the Revenue page.
  param: P = 'tab' as P,
) {
  type Search = { [K in P]?: T[number] };
  return (search: Record<string, unknown>): Search => {
    const t = search[param];
    if (has(valid, t) && t !== valid[0]) return { [param]: t } as Search;
    return {} as Search;
  };
}

/**
 * One choice kept in the page's address, read from it and written back to it: a tab, the
 * Revenue page's period, the Jobs list's service type filter. `read` keeps what the route's own
 * `validateSearch` keeps (the value, or undefined for junk); `fallback` is the value when the
 * address says nothing, and is never written into it.
 *
 * Written back with `replace` — switching a tab or a filter is not a place the Back button
 * should revisit one click at a time. `resetScroll: false` for the same reason Sonar uses bare
 * `replaceState`: the control must not jump away under the pointer that just clicked it.
 *
 * `strict: false` because this hook serves many routes — the route's own `validateSearch` has
 * already thrown junk away; `read` here is what types the value without pinning the hook to one
 * route id. The write is a FUNCTIONAL updater over the current search, so another search param
 * on the same route survives a click instead of being wiped by a `{ tab }` literal (review 105
 * m1).
 */
export function useUrlParam<V extends string>(
  param: string,
  read: (raw: unknown) => V | undefined,
  fallback: V,
): [V, (next: string) => void] {
  const search: Record<string, unknown> = useSearch({ strict: false });
  const navigate = useNavigate();
  const value = read(search[param]) ?? fallback;
  const setValue = (next: string) => {
    if (next !== fallback && read(next) === undefined) return;
    // The updater is cast because the hook is route-generic by design — `to: '.'`
    // gives the type system no route to check the search against. The value is still
    // validated twice at runtime: `read` above, and the destination route's own
    // `validateSearch` when the navigation lands.
    const merge = (prev: Record<string, unknown>) => {
      const { [param]: _drop, ...rest } = prev;
      return next === fallback ? rest : { ...rest, [param]: next };
    };
    void navigate({
      to: '.',
      search: merge as never,
      replace: true,
      resetScroll: false,
    });
  };
  return [value, setValue];
}

/**
 * `/reports/revenue`'s `validateSearch` (ops insights Phase 2, tickets 02 and 07): its period,
 * `period` (a `REPORT_PERIODS` key), and its basis, `basis` (`REVENUE_BASES`: Invoiced or
 * Collected), each on `tabSearch`'s contract: a value other than the default is kept, and the
 * default, junk and absence are all left out, so `/reports/revenue` is This month on Invoiced
 * and a link with `?basis=collected` opens the same view for whoever it is sent to.
 */
export function revenueSearch(search: Record<string, unknown>) {
  return {
    ...tabSearch(REPORT_PERIODS, 'period')(search),
    ...tabSearch(REVENUE_BASES, 'basis')(search),
  };
}

/** The tab (or another choice under its own `param`) in the URL, as `tabSearch` validates it. */
export function useUrlTab<T extends readonly string[]>(
  valid: T,
  // The search parameter it reads and writes, as `tabSearch` validates it.
  param = 'tab',
): [T[number], (next: string) => void] {
  return useUrlParam<T[number]>(
    param,
    (raw) => (has(valid, raw) ? raw : undefined),
    valid[0] as T[number],
  );
}

/** The Jobs list's service type filter in its address: "none" or a type's id, else nothing. */
const serviceTypeIn = (raw: unknown): string | undefined =>
  serviceTypeFilterValue.safeParse(raw).success ? (raw as string) : undefined;

/**
 * `/jobs`' `validateSearch` (ops insights Phase 2, ticket 06), on the same contract as
 * `tabSearch`: a value the list's own input takes (`serviceTypeFilterValue`: "none" or a type's
 * id) is kept, and junk, absence and every job ('') are `{}`, so `/jobs` is every job. The
 * Revenue page's "Classify these jobs" opens `/jobs?serviceType=none`.
 */
export function serviceTypeSearch(search: Record<string, unknown>): {
  serviceType?: string;
} {
  const value = serviceTypeIn(search.serviceType);
  return value === undefined ? {} : { serviceType: value };
}

/**
 * The Jobs list's service type filter, read from its address and written back to it; '' is
 * every job, and leaves the parameter out.
 */
export function useUrlServiceType(): [string, (next: string) => void] {
  return useUrlParam('serviceType', serviceTypeIn, '');
}

/** Separate keys keep historical /jobs?serviceType=none execution links meaningful. */
export function workspaceId(raw: unknown): string | undefined {
  return typeof raw === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)
    ? raw
    : undefined;
}
export function jobsWorkspaceSearch(search: Record<string, unknown>) {
  const result: Record<string, unknown> = {
    ...serviceTypeSearch(search),
    ...workspaceTableSearch(search),
  };
  // An explicit shared List link must beat the recipient's saved Pipeline preference.
  if (has(JOB_WORKSPACE_VIEWS, search.view)) result.view = search.view;
  for (const key of ['pipelineId', 'ownerUserId', 'serviceTypeId']) {
    const value = workspaceId(search[key]);
    if (value) result[key] = value;
  }
  if (search.outcome === 'open' || search.outcome === 'won' || search.outcome === 'lost')
    result.outcome = search.outcome;
  return result;
}

export function quotesWorkspaceSearch(search: Record<string, unknown>) {
  const result: Record<string, unknown> = { ...workspaceTableSearch(search) };
  for (const key of ['pipelineId', 'ownerUserId']) {
    const value = workspaceId(search[key]);
    if (value) result[key] = value;
  }
  if (has(QUOTE_STATES, search.status)) result.status = search.status;
  return result;
}
